import cn from 'classnames';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { isRespSuccess } from '@webapp/account/src/libs/request';
import { ResultsContext } from '@webapp/account/src/pages/survey/results/context';
import {
    Actions,
    FilterFieldType,
    getPeriodLabel,
    getPeriodName,
    removeFilter,
    selectLinkParamFilters,
    selectReports,
} from '@webapp/account/src/resources/reports';
import { createFilterGroup, getFilterGroups } from '@webapp/account/src/resources/reports/async-filters';
import type { GlobalFilter } from '@webapp/account/src/resources/reports/global-filters';
import { ReportStatus } from '@webapp/account/src/resources/reports/reports';
import { selectWithFilters } from '@webapp/account/src/resources/reports/selectors';
import { selectMinFilterDate } from '@webapp/account/src/resources/survey/selectors';

import { CalendarDates, Filter } from '@webapp/common/assets/icons';
import { Button } from '@webapp/common/components/button';
import { Checkbox } from '@webapp/common/components/checkbox';
import { CloseBtn } from '@webapp/common/components/close-btn';
import { EditName } from '@webapp/common/components/edit-name';
import { Scrollarea } from '@webapp/common/components/scrollarea';
import { Tooltip } from '@webapp/common/components/tooltip';
import { useAction, useActions, useAsync } from '@webapp/common/hooks/use-actions';
import { useFixedSticky } from '@webapp/common/hooks/use-fixed-sticky';
import { useRouteId } from '@webapp/common/hooks/use-route-id';
import { UI_DATE_FORMAT } from '@webapp/common/lib/date';

import { FilterGroups } from './filter-groups';
import { FilterLine } from './filter-line';

import css from './applied-filters.css';

export const AppliedFilters: FC<{
    visible: boolean;
    headerHeight: number;
    isPublic?: boolean;
    filterGroup?: GlobalFilter;
}> = ({ headerHeight, visible: filtersVisible, isPublic, filterGroup }) => {
    const { id: surveyId } = useRouteId();
    const panelRef = useRef(null);
    const [panelHeight, setPanelHeight] = useState<number>(0);
    const [showSaveFilterInput, setShowSaveFilterInput] = useState<boolean>(false);
    const saMaxHeight = panelHeight - 300;
    const style = useFixedSticky(headerHeight, true);

    const {
        globalFilters: { dateFrom, dateTo, filters, tags, reportStatus, comparePeriods },
        resetFilters,
    } = useContext(ResultsContext);
    const { fetchReports, filterGroups } = useSelector(selectReports);
    const selectHasFilters = useSelector(selectWithFilters);
    const paramsFiltersFormatted = useSelector(selectLinkParamFilters);
    const surveyCreatedDate = useSelector(selectMinFilterDate);

    const hasParamsFilters = paramsFiltersFormatted.length !== 0;
    const tagsFilterArray = Object.entries(tags);
    const hasTagsFilters = tagsFilterArray.length > 0;
    const hasFilters = selectHasFilters || hasParamsFilters || hasTagsFilters || reportStatus !== ReportStatus.ALL;

    const onRemove = useAsync(removeFilter);
    const setFiltersVisible = useAction(Actions.setFiltersVisible);
    const filterParamRemove = useAction(Actions.removeParamFilter);
    const toggleFilterWordCloudTag = useAction(Actions.toggleFilterWordCloudTag);
    const createFilterGroupRequest = useActions(createFilterGroup);
    const getFilterGroupsRequest = useActions(getFilterGroups);

    const handleRemove = useCallback(
        (filter, set) =>
            onRemove({
                filter,
                set,
            }),
        [onRemove],
    );

    const handleToggleFilterWordCloudTag = useCallback(
        (tag, set) => {
            toggleFilterWordCloudTag({
                reportId: tag.reportId,
                filterFieldType: set.comment ? FilterFieldType.COMMENT_CLOUD : FilterFieldType.WORD_CLOUD,
                text: set.text,
            });
        },
        [toggleFilterWordCloudTag],
    );

    const toggleVisible = useCallback(() => setFiltersVisible(!filtersVisible), [filtersVisible, setFiltersVisible]);
    const close = useCallback(() => setFiltersVisible(false), [setFiltersVisible]);
    const reset = useCallback(() => resetFilters({ surveyCreatedDate }), [resetFilters]);

    const removeLinkParam = useCallback(
        (filter, set) => filterParamRemove({ paramId: filter.id, value: set.name }),
        [filterParamRemove],
    );

    useEffect(() => {
        if (filtersVisible && filterGroups.surveyId !== surveyId) {
            getFilterGroupsRequest({ surveyId });
        }
    }, [getFilterGroupsRequest, filtersVisible, surveyId, filterGroups.surveyId]);

    useEffect(() => {
        setPanelHeight(panelRef.current.offsetHeight);
    }, [style]);

    const renderSaveFilterGroup = () => {
        return showSaveFilterInput ? (
            <div>
                <EditName
                    placeholder='Название группы'
                    className={css.filterGroupInput}
                    name=''
                    onDone={() => {
                        setShowSaveFilterInput(false);
                    }}
                    onSave={(name) => {
                        createFilterGroupRequest({ surveyId, name }).then((resp) => {
                            if (isRespSuccess(resp)) {
                                getFilterGroupsRequest({ surveyId });
                            }
                        });
                    }}
                />
            </div>
        ) : (
            <div className={css.saveFiltersWrap}>
                <Checkbox
                    label='Сохранить фильтры'
                    onChange={() => {
                        setShowSaveFilterInput(true);
                    }}
                />
                <Tooltip withTooltipIcon={true} clickable={true}>
                    Сохраните группы фильтров и делитесь ими через{' '}
                    <a
                        href='https://www.testograf.ru/ru/help?#kak-podelitsya-rezultatami-oprosa'
                        target='_blank'
                        rel='noreferrer'
                    >
                        публичные ссылки
                    </a>{' '}
                    на результаты
                </Tooltip>
            </div>
        );
    };

    const renderResetFilters = () => {
        if (isPublic && filterGroup) return null;

        return (
            <div className={css.reset}>
                <Button disabled={showSaveFilterInput} fluid className={css.btn} color='blue' onClick={reset}>
                    Сбросить фильтры
                </Button>
            </div>
        );
    };

    return (
        <div className={cn(css.filters, { [css.visible]: filtersVisible })}>
            <div className={css.tag} onClick={toggleVisible}>
                <div className={cn(css.label, { [css.active]: hasFilters })}>
                    <div className={css.activeIndicator} />
                    <Filter />
                    <span>Фильтры</span>
                </div>
            </div>
            <div
                ref={panelRef}
                style={style}
                className={cn(css.panel, {
                    [css.visible]: filtersVisible,
                })}
            >
                <div className={css.header}>
                    <CloseBtn className={css.close} onClick={close} />
                    <div className={css.title}>Фильтры</div>
                    <div className={css.date}>
                        <CalendarDates />
                        <span>
                            {dayjs(dateFrom).format(UI_DATE_FORMAT)} - {dayjs(dateTo).format(UI_DATE_FORMAT)}
                        </span>
                    </div>
                    {comparePeriods?.length > 0 && (
                        <div className={css.compare}>
                            {comparePeriods.map((period, index) => (
                                <span className={css.compareItem} key={period.id}>
                                    {getPeriodName(period, index + 1)} · {getPeriodLabel(period)}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <hr />
                <Scrollarea className={css.scrollBlock} horizontal={false} maxHeight={saMaxHeight}>
                    {!isPublic && filterGroups.list.length > 0 && (
                        <>
                            <div>
                                <div className={css.filtersTitle}>Группы фильтров</div>
                                <FilterGroups filterGroups={filterGroups.list} surveyId={surveyId} />
                            </div>
                            <hr />
                        </>
                    )}
                    {paramsFiltersFormatted?.length > 0 && (
                        <div>
                            <div className={css.filtersTitle}>Дополнительные ссылки</div>
                            <div className={css.filtersWrap}>
                                {paramsFiltersFormatted.map((filter, filterIndex) => (
                                    <FilterLine
                                        filter={filter}
                                        filterIndex={filterIndex}
                                        key={filterIndex}
                                        loading={false}
                                        onRemove={removeLinkParam}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {filters.length > 0 && (
                        <div>
                            <div className={css.filtersTitle}>Вопросы</div>
                            <div className={css.filtersWrap}>
                                {filters.map((filter, filterIndex) => (
                                    <FilterLine
                                        filter={filter}
                                        filterIndex={filterIndex}
                                        key={`filter-${filterIndex}`}
                                        loading={fetchReports}
                                        onRemove={handleRemove}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {hasTagsFilters && (
                        <div>
                            <div className={css.filtersTitle}>Теги</div>
                            <div className={css.filtersWrap}>
                                {tagsFilterArray.map(([reportId, tag]: any, tagIndex) => (
                                    <FilterLine
                                        filter={{ reportId, sets: tag.items, id: tag.name, name: tag.name }}
                                        filterIndex={tagIndex}
                                        key={`tag-${tagIndex}`}
                                        loading={fetchReports}
                                        onRemove={handleToggleFilterWordCloudTag}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </Scrollarea>
                {!isPublic && hasFilters && renderSaveFilterGroup()}
                {hasFilters && renderResetFilters()}
            </div>
        </div>
    );
};
